import { Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';

const HERO_IMG = 'https://readdy.ai/api/search-image?query=Complete%20fertility%20surrogacy%20journey%20family%20with%20newborn%20baby%2C%20warm%20hospital%20scene%20with%20caring%20medical%20team%2C%20champagne%20gold%20and%20white%20tones%2C%20emotional%20joyful%20parents%20holding%20newborn%2C%20professional%20medical%20care%2C%20soft%20natural%20lighting%2C%20hopeful%20atmosphere&width=1600&height=700&seq=full-hero-2026&orientation=landscape';

const STEP_IMGS = [
  'https://readdy.ai/api/search-image?query=Fertility%20doctor%20couple%20consultation%20IVF%20surrogacy%20plan%2C%20professional%20medical%20office%20warm%20champagne%20light%2C%20doctor%20explaining%20comprehensive%20plan%20to%20couple%2C%20modern%20clean%20interior&width=600&height=400&seq=full-step-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Ovarian%20stimulation%20monitoring%20ultrasound%20scan%2C%20fertility%20clinic%20ultrasound%20room%2C%20doctor%20nurse%20patient%20monitoring%20follicle%20growth%2C%20professional%20warm%20tones&width=600&height=400&seq=full-step-02&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Egg%20retrieval%20procedure%20operating%20room%20medical%20team%2C%20fertility%20clinic%20professional%20surgical%20team%2C%20sterile%20clean%20environment%20white%20tones%2C%20warm%20ambient%20lighting&width=600&height=400&seq=full-step-03&orientation=landscape',
  'https://readdy.ai/api/search-image?query=IVF%20fertilization%20sperm%20egg%20embryo%20culture%20laboratory%2C%20embryologist%20microscope%20embryo%20monitoring%2C%20clean%20white%20high-tech%20lab%2C%20professional%20scientific%20precision&width=600&height=400&seq=full-step-04&orientation=landscape',
  'https://readdy.ai/api/search-image?query=PGT%20genetic%20testing%20embryo%20biopsy%2C%20embryologist%20with%20fine%20needle%20extracting%20single%20cell%20from%20embryo%2C%20high%20magnification%20microscope%2C%20clean%20lab%20warm%20tones&width=600&height=400&seq=full-step-05&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Embryo%20transfer%20medical%20procedure%20fertility%20clinic%2C%20doctor%20performing%20embryo%20transfer%20with%20catheter%20ultrasound%20monitor%2C%20professional%20sterile%20medical%20room%20warm%20white%20tones&width=600&height=400&seq=full-step-06&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Pregnant%20surrogate%20mother%20prenatal%20care%20check%20up%2C%20warm%20clinic%20room%2C%20caring%20doctor%20monitoring%20pregnancy%2C%20soft%20champagne%20cream%20light%2C%20professional%20medical%20team&width=600&height=400&seq=full-step-07&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Newborn%20baby%20hospital%20birth%20parents%20holding%20baby%2C%20emotional%20joyful%20moment%20maternity%20ward%2C%20warm%20soft%20lighting%20champagne%20cream%20tones%2C%20happy%20new%20parents%20professional%20medical%20care&width=600&height=400&seq=full-step-08&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Family%20passport%20legal%20documents%20baby%20travel%2C%20official%20legal%20papers%20stamped%2C%20attorney%20office%2C%20parents%20preparing%20to%20travel%20home%20with%20newborn%2C%20warm%20professional%20atmosphere&width=600&height=400&seq=full-step-09&orientation=landscape',
];

const fullJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: '试管助孕 - RTB吉尔吉斯斯坦试管代孕中心',
    description: '试管+代孕全链条服务，惠享$82,000/安心$89,800/无忧$102,000，9步全流程，从促排取卵到宝宝回国。',
    url: `${SITE_URL}/fertility`,
  },
];

export default function FullServiceDetailPage() {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  const steps = useMemo(() => Array.from({ length: 9 }, (_, i) => {
    const n = i + 1;
    return {
      num: `0${n}`,
      title: t(`full_step${n}_title`),
      duration: t(`full_step${n}_dur`),
      desc: t(`full_step${n}_desc`),
      points: [t(`full_step${n}_p1`), t(`full_step${n}_p2`), t(`full_step${n}_p3`), t(`full_step${n}_p4`)],
    };
  }), [t]);

  const pricingPlans = useMemo(() => [
    {
      level: t('full_plan_economy'),
      price: '$82,000',
      unit: 'USD',
      badge: t('full_plan_economy_badge'),
      desc: t('full_plan_economy_desc'),
      items: Array.from({ length: 10 }, (_, i) => t(`full_plan_economy_item${i + 1}`)),
      highlight: false,
    },
    {
      level: t('full_plan_premium'),
      price: '$89,800',
      unit: 'USD',
      badge: t('full_plan_premium_badge'),
      desc: t('full_plan_premium_desc'),
      items: Array.from({ length: 10 }, (_, i) => t(`full_plan_premium_item${i + 1}`)),
      highlight: true,
    },
    {
      level: t('full_plan_comprehensive'),
      price: '$102,000',
      unit: 'USD',
      badge: t('full_plan_comprehensive_badge'),
      desc: t('full_plan_comprehensive_desc'),
      items: Array.from({ length: 10 }, (_, i) => t(`full_plan_comp_item${i + 1}`)),
      highlight: false,
    },
  ], [t]);

  const planNoteIncItems = useMemo(() => Array.from({ length: 10 }, (_, i) => t(`full_plan_note_inc_item${i + 1}`)), [t]);
  const planNoteExtraItems = useMemo(() => Array.from({ length: 8 }, (_, i) => t(`full_plan_note_extra_item${i + 1}`)), [t]);

  return (
    <>
      <SeoHead
        title="试管助孕｜$82,000起全链条方案 - RTB吉尔吉斯斯坦试管代孕中心"
        description="RTB吉尔吉斯斯坦试管助孕：三代试管+代孕全链条服务，惠享$82,000/安心$89,800/无忧$102,000，9步全流程，从促排取卵到宝宝回国一站式圆梦。"
        keywords="试管助孕,三代试管代孕,PGT-A代孕,吉尔吉斯斯坦试管助孕,试管助孕费用"
        canonical="/fertility"
        jsonLd={fullJsonLd}
      />
      <main className="pt-16 md:pt-20">
        {/* Hero */}
        <section className="relative h-[420px] md:h-[580px] flex items-center overflow-hidden">
          <img src={HERO_IMG} alt="试管助孕" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2137]/80 via-[#0d2137]/50 to-transparent"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 mb-4">
                <Link to="/services" className="text-[#c9a96e] text-xs hover:underline cursor-pointer">{t('ivf_breadcrumb_svc')}</Link>
                <i className="ri-arrow-right-s-line text-[#c9a96e] text-xs"></i>
                <span className="text-white/70 text-xs">{t('full_breadcrumb')}</span>
              </div>
              <div className="text-[#c9a96e] text-sm font-medium tracking-widest uppercase mb-3">{t('full_label')}</div>
              <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4">{t('full_page_title')}</h1>
              <p className="text-white/80 text-sm md:text-base leading-relaxed mb-8">
                {t('full_hero_desc')}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" className="inline-flex items-center gap-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold px-7 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm">
                  <i className="ri-calendar-line"></i> {t('ivf_cta_consult')}
                </Link>
                <a href="https://wa.me/8618513885468" target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium px-7 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm backdrop-blur-sm">
                  <i className="ri-whatsapp-line"></i> {t('ivf_cta_whatsapp')}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Key stats */}
        <section className="bg-[#1a3a5c] py-8">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { num: '$82,000', labelKey: 'full_stat_price' },
                { num: '3档', labelKey: 'full_stat_plans' },
                { num: '9步', labelKey: 'full_stat_steps' },
                { num: '100%', labelKey: 'full_stat_legal' },
              ].map((s) => (
                <div key={s.labelKey}>
                  <div className="text-2xl md:text-3xl font-bold text-[#c9a96e] mb-1">{s.num}</div>
                  <div className="text-white/70 text-xs">{t(s.labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 md:py-24 bg-[#f8f5f0]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <div className="text-[#c9a96e] text-xs font-medium tracking-widest uppercase mb-2">{t('full_process_label')}</div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-3">{t('full_process_title')}</h2>
              <p className="text-gray-500 text-sm">{t('full_process_sub')}</p>
            </div>

            <div className="mb-10">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {steps.map((s, i) => (
                  <button key={i} onClick={() => setActiveStep(i)} className={`text-left p-4 rounded-xl transition-all cursor-pointer ${activeStep === i ? 'bg-[#1a3a5c] text-white' : 'bg-white hover:bg-[#f0e8d8]'}`}>
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full mb-3 text-sm font-bold ${activeStep === i ? 'bg-[#c9a96e] text-white' : 'bg-[#f8f5f0] text-[#c9a96e]'}`}>{s.num}</div>
                    <div className={`text-xs font-semibold mb-1 ${activeStep === i ? 'text-white' : 'text-[#1a3a5c]'}`}>{s.title}</div>
                    <div className={`text-xs ${activeStep === i ? 'text-white/60' : 'text-gray-400'}`}>{s.duration}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="h-64 md:h-80 lg:h-auto overflow-hidden">
                  <img src={STEP_IMGS[activeStep]} alt={steps[activeStep].title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl font-bold text-[#c9a96e]/30">{steps[activeStep].num}</span>
                    <div>
                      <div className="text-xs text-[#c9a96e] font-medium mb-0.5">{steps[activeStep].duration}</div>
                      <h3 className="text-xl md:text-2xl font-bold text-[#1a3a5c]">{steps[activeStep].title}</h3>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">{steps[activeStep].desc}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {steps[activeStep].points.map((p) => (
                      <div key={p} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-checkbox-circle-fill text-[#c9a96e] shrink-0 text-base"></i>{p}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-8">
                    <button onClick={() => setActiveStep((prev) => Math.max(0, prev - 1))} disabled={activeStep === 0} className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:border-[#1a3a5c] hover:text-[#1a3a5c] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <button onClick={() => setActiveStep((prev) => Math.min(steps.length - 1, prev + 1))} disabled={activeStep === steps.length - 1} className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1a3a5c] text-white hover:bg-[#2a5a8c] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                    <span className="ml-auto text-xs text-gray-400 self-center">{activeStep + 1} / {steps.length}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <div className="text-[#c9a96e] text-xs font-medium tracking-widest uppercase mb-2">{t('full_plan_section_label')}</div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-3">{t('full_plan_section_title')}</h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">{t('full_plan_section_sub')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {pricingPlans.map((plan) => (
                <div
                  key={plan.level}
                  className={`relative rounded-2xl overflow-hidden border-2 transition-all ${plan.highlight ? 'border-[#c9a96e] bg-[#1a3a5c]' : 'border-gray-100 bg-[#f8f5f0]'}`}
                >
                  {plan.badge && (
                    <div className="absolute top-4 right-4 bg-[#c9a96e] text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {plan.badge}
                    </div>
                  )}
                  <div className="p-6 md:p-8">
                    <div className="text-xs font-medium tracking-widest uppercase mb-2 text-[#c9a96e]">{plan.level}</div>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className={`text-3xl font-bold ${plan.highlight ? 'text-white' : 'text-[#1a3a5c]'}`}>{plan.price}</span>
                      <span className={`text-sm ${plan.highlight ? 'text-white/70' : 'text-gray-400'}`}>{plan.unit}</span>
                    </div>
                    <p className={`text-xs mb-6 ${plan.highlight ? 'text-white/60' : 'text-gray-500'}`}>{plan.desc}</p>
                    <div className="space-y-2.5 mb-7">
                      {plan.items.map((item) => (
                        <div key={item} className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-[#c9a96e] shrink-0 mt-0.5"></i>
                          <span className={`text-xs ${plan.highlight ? 'text-white/80' : 'text-gray-600'}`}>{item}</span>
                        </div>
                      ))}
                    </div>
                    <a href="/contact" className={`block text-center py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${plan.highlight ? 'bg-[#c9a96e] hover:bg-[#b8944f] text-white' : 'bg-[#1a3a5c] hover:bg-[#2a4a6c] text-white'}`}>
                      {t('full_plan_btn')}
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-[#f8f5f0] rounded-2xl p-6 md:p-8">
              <div className="flex items-start gap-3 mb-4">
                <i className="ri-information-line text-[#c9a96e] text-xl shrink-0 mt-0.5"></i>
                <h3 className="font-bold text-[#1a3a5c] text-sm">{t('full_plan_note_title')}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs font-semibold text-[#1a3a5c] mb-3">{t('full_plan_note_inc_title')}</div>
                  <div className="space-y-1.5">
                    {planNoteIncItems.map((i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-check-line text-green-500"></i>{i}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-[#1a3a5c] mb-3">{t('full_plan_note_extra_title')}</div>
                  <div className="space-y-1.5">
                    {planNoteExtraItems.map((i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-information-line text-[#c9a96e]"></i>{i}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-5 pt-5 border-t border-gray-200 text-xs text-gray-400">
                {t('full_plan_note_footer')}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#1a3a5c]">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('full_cta_title')}</h2>
            <p className="text-white/70 text-sm mb-8">{t('full_cta_desc')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold px-8 py-3.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-calendar-check-line"></i> {t('ivf_cta_consult')}
              </Link>
              <a href="https://wa.me/8618513885468" target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium px-8 py-3.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-whatsapp-line"></i> {t('ivf_cta_whatsapp')}
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}